import { Box, Button, IconButton, Typography, Stack, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { HangarIcon } from '../../widgets/Icons/HangarIcon';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Hangar } from '../../types';
import { MultiHangarPresenter } from '../MultiHangar/MultiHangarPresenter';
import { HangarPicker } from './components/HangarPicker';
import { OpsQueue } from './components/OpsQueue/OpsQueue';

const QUEUE_WIDTH = 350;
const QUEUE_COLLAPSED_WIDTH = 48;

type Props = {
  selectedHangarIds: string[];
  availableHangars: Hangar[];
  pickerOpen: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onSelectHangars: (hangarIds: string[]) => void;
};

/**
 * OpsPresenter - Layout component for the Ops view
 * Renders queue panel on left + MultiHangarPresenter on right
 */
export const OpsPresenter: React.FC<Props> = ({
  selectedHangarIds,
  availableHangars,
  pickerOpen,
  onOpenPicker,
  onClosePicker,
  onSelectHangars,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [queueCollapsed, setQueueCollapsed] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Callback to refresh hangars after assignment - increments key to trigger remount
  const handleAssignmentComplete = React.useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden' }}>
      {/* Queue Panel (Left) */}
      <Box
        sx={{
          width: queueCollapsed ? QUEUE_COLLAPSED_WIDTH : QUEUE_WIDTH,
          minWidth: queueCollapsed ? QUEUE_COLLAPSED_WIDTH : QUEUE_WIDTH,
          height: '100%',
          borderRight: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        {/* Queue Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: queueCollapsed ? 'center' : 'space-between',
            px: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            height: 49,
          }}
        >
          {!queueCollapsed && (
            <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
              {t('screens.ops.queue.title')}
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={() => setQueueCollapsed(!queueCollapsed)}
            title={queueCollapsed ? t('screens.ops.queue.expand', 'Expand queue') : t('screens.ops.queue.collapse', 'Collapse queue')}
          >
            {queueCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* Queue Content */}
        {!queueCollapsed && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <OpsQueue
              selectedHangarIds={selectedHangarIds}
              onAssignmentComplete={handleAssignmentComplete}
            />
          </Box>
        )}
      </Box>

      {/* Main Content (Hangars) */}
      <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        {/* Header with hangar picker button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            height: 49,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<HangarIcon />}
            onClick={onOpenPicker}
          >
            {selectedHangarIds.length > 0
              ? t('screens.ops.hangarsSelected', { count: selectedHangarIds.length })
              : t('screens.ops.noHangars.action')}
          </Button>
        </Box>

        {/* Hangar Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {selectedHangarIds.length > 0 ? (
            <MultiHangarPresenter key={refreshKey} hangarIds={selectedHangarIds} opsMode />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: '100%', gap: 2 }}
            >
              <HangarIcon sx={{ fontSize: 64, color: theme.palette.text.secondary }} />
              <Typography variant="h6" color="text.secondary">
                {t('screens.ops.noHangars.title')}
              </Typography>
              <Button variant="contained" onClick={onOpenPicker}>
                {t('screens.ops.noHangars.action')}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Hangar Picker Dialog */}
      <HangarPicker
        open={pickerOpen}
        onClose={onClosePicker}
        availableHangars={availableHangars}
        selectedHangarIds={selectedHangarIds}
        onConfirm={onSelectHangars}
      />
    </Box>
  );
};
